import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { pageMeta } from "@/data/mockData";
import { useAwards } from "@/lib/content";

export default function Awards() {
  const { data: awards } = useAwards();
  const sorted = [...awards].sort((a, b) => {
    const dateA = a.awardDate || a.uploadedAt || "";
    const dateB = b.awardDate || b.uploadedAt || "";
    return dateB.localeCompare(dateA);
  });

  return (
    <>
      <SEO {...pageMeta.awards} />

      <section className="bg-white pt-20 pb-12 md:py-24" data-testid="awards-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
              Recognition
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.05]">
              Awards & Achievements.
            </h1>
            <p className="mt-7 text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              A decade of consistent client outcomes, recognised year after year by Singapore's leading agencies.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#fafafa] py-16 md:py-24" data-testid="awards-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/*
            BACKEND INTEGRATION:
            - Replace `awards` with a fetch to /api/awards
            - Each item should provide: { id, image, title?, year?, uploadedAt }
            - Frontend already sorts by uploadedAt descending (newest first).
            - Image-only display per spec; title/year shown only if available.
          */}
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {sorted.map((a, i) => (
              <motion.figure
                key={a.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                className="mb-5 break-inside-avoid bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow group"
                data-testid={`award-${a.id}`}
              >
                <div className="overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.title || "Award"}
                    loading="lazy"
                    className="w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                {(a.title || a.year) && (
                  <figcaption className="px-5 py-4">
                    {a.title && (
                      <div className="font-serif text-base text-[#0a2540] leading-tight">{a.title}</div>
                    )}
                    {a.year && (
                      <div className="text-xs text-slate-500 mt-0.5 font-medium tracking-wider">{a.year}</div>
                    )}
                  </figcaption>
                )}
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
